// UmfrageCard — interne Patienten-Umfrage: Link/QR für den Empfang und
// Versand per E-Mail. Antworten landen als interne Bewertungen (anonym),
// optional mit Behandler-Zuordnung über den Link-Token.
import { useState, useEffect, useMemo } from "react";
import { Card, CardHead, Btn, Select, Insight, Tbl, Td, Sp, toast } from "./ui";
import { getPraxis, getBehandler, getSetting, saveSetting } from "./lib/settingsStore";
import { supabaseUrl } from "./lib/supabase";
import { serverActive } from "./lib/mode";
import { sendMails, findPatientEmail, praxisAbsender } from "./lib/mailer";
import { listApts } from "./lib/aptStore";
import { listPatients } from "./lib/patientStore";
import PatientPicker from "./PatientPicker";

const buildToken = (behandlerName) => {
  const payload = { p: getPraxis().name || "", ...(behandlerName ? { b: behandlerName } : {}) };
  try { return btoa(unescape(encodeURIComponent(JSON.stringify(payload)))); } catch { return ""; }
};
// Die Umfrageseite wird als statische Datei von der eigenen Domain
// ausgeliefert (public/umfrage.html) — Supabase blockiert HTML aus Edge
// Functions (Anti-Phishing → text/plain). Gespeichert wird weiterhin über
// die Edge Function, die umfrage.html per POST anspricht.
export const umfrageUrl = (behandlerName) =>
  supabaseUrl ? `${window.location.origin}/umfrage.html?t=${buildToken(behandlerName)}` : "";

export default function UmfrageCard() {
  const [beh, setBeh] = useState("");
  const [mailPat, setMailPat] = useState("");
  const url = umfrageUrl(beh);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast("Link kopiert"); }
    catch { toast("Kopieren fehlgeschlagen — Link manuell markieren.", "r"); }
  };

  const sendSurveyMail = async () => {
    if (!mailPat) { toast("Zuerst Patient wählen.", "r"); return; }
    const email = findPatientEmail(mailPat);
    if (serverActive() && !email) { toast(`Für ${mailPat} ist keine E-Mail hinterlegt.`, "r"); return; }
    try {
      const res = await sendMails([{
        to: email || "demo@dida.local", toName: mailPat,
        subject: "Wie war Ihr Besuch bei uns? — 1 Minute Feedback",
        text: `Liebe/r ${mailPat.split(",")[1]?.trim() || mailPat},\n\nvielen Dank für Ihren Besuch! Uns interessiert, wie zufrieden Sie waren — die Beantwortung dauert unter einer Minute und ist anonym:\n\n${url}\n\nHerzlichen Dank!\n\nMit freundlichen Grüßen\n${praxisAbsender()}`,
      }]);
      toast(res.simulated ? "Demo — Versand simuliert" : "Umfrage-Mail gesendet ✓");
      setMailPat("");
    } catch (e) { toast(`Versand fehlgeschlagen: ${e.message}`, "r"); }
  };

  if (!supabaseUrl) return (
    <Card><CardHead title="Interne Umfrage" icon="ti-clipboard-heart"/>
      <Insight tone="a">Die Patienten-Umfrage braucht den Live-Modus (Supabase) — im Demo-Modus nicht verfügbar.</Insight>
    </Card>
  );

  return (
    <Card>
      <CardHead title="Interne Umfrage" icon="ti-clipboard-heart"/>
      <div style={{fontSize:11,color:"var(--mf-muted)",marginBottom:10,lineHeight:1.5}}>
        Anonyme 1-Minuten-Umfrage (Gesamtzufriedenheit, Weiterempfehlung, Freitext). Antworten erscheinen automatisch unter den Bewertungen — mit Behandler-Zuordnung, wenn unten ein Behandler gewählt ist.
      </div>
      <Select label="Zuordnung" value={beh} onChange={setBeh}
        options={[{value:"",label:"Ganze Praxis (ohne Behandler)"},...getBehandler().map(b=>({value:b.name,label:b.name}))]}/>
      <div>
        <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:4}}>Link — zum Einbauen in Terminbestätigungen, Rechnungs-Mails oder die Praxis-Website</div>
        <div style={{fontSize:10.5,fontFamily:"ui-monospace,monospace",background:"var(--mf-bg-alt)",borderRadius:10,padding:"8px 10px",wordBreak:"break-all",color:"var(--mf-text)"}}>{url}</div>
        <div style={{display:"flex",gap:6,marginTop:8}}>
          <Btn color="g" onClick={copy}>Link kopieren</Btn>
          <Btn onClick={()=>window.open(url,"_blank")}>Vorschau öffnen</Btn>
        </div>
      </div>
      <div style={{borderTop:"0.5px solid var(--mf-border)",marginTop:12,paddingTop:10}}>
        <div style={{fontSize:10,color:"var(--mf-muted)",marginBottom:6}}>Oder direkt per E-Mail an einen Patienten senden:</div>
        <div style={{display:"flex",gap:8,alignItems:"flex-start",flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:200}}><PatientPicker label="Patient" value={mailPat} onChange={setMailPat}/></div>
          <Btn color="g" lg onClick={sendSurveyMail}>Umfrage-Mail senden</Btn>
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UMFRAGE NACH TERMIN
// ═══════════════════════════════════════════════════════════════════════════
// Sammelt die Patienten der letzten Tage und verschickt die Umfrage in einem
// Rutsch — mit dem Behandler des jeweiligen Termins im Link, damit die
// Antworten ohne Zutun beim richtigen Behandler landen.
//
// Bewusst KEIN Hintergrundjob: Der Versand passiert, wenn hier jemand auf den
// Knopf drückt. Ein echter Automatikversand bräuchte einen Zeitplan in
// Supabase (pg_cron + Edge Function) — das wäre einzurichten, bevor man es
// "automatisch" nennen darf.

const SENT_KEY = "umfrage_versand";   // { [terminId]: "JJJJ-MM-TT" }
const WINDOWS = [3, 7, 14];

// Termine haben keinen Status "durchgeführt" — als stattgefunden gilt
// deshalb: liegt in der Vergangenheit und wurde nicht abgesagt.
const isoDay = (offsetTage = 0) =>
  new Date(Date.now() + offsetTage * 86400000).toISOString().slice(0, 10);

const behandlerName = (id) => getBehandler().find(b => b.id === id)?.name || "";

export function UmfrageNachTerminCard() {
  const [tage, setTage] = useState(7);
  const [apts, setApts] = useState([]);
  const [sent, setSent] = useState(() => getSetting(SENT_KEY) || {});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let on = true;
    Promise.allSettled([listPatients(), listApts()]).then(([, a]) => {
      if (on && a.status === "fulfilled") setApts(a.value);
    });
    return () => { on = false; };
  }, []);

  // Ein Patient bekommt höchstens eine Umfrage — bei mehreren Terminen im
  // Zeitraum zählt der jüngste (danach hat er den frischesten Eindruck).
  const kandidaten = useMemo(() => {
    const von = isoDay(-tage), bis = isoDay(0);
    const proPatient = new Map();
    apts
      .filter(a => a.date >= von && a.date <= bis && a.status !== "abgesagt" && (a.patient || "").trim())
      .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`))
      .forEach(a => proPatient.set(a.patient.trim().toLowerCase(), a));
    return [...proPatient.values()]
      .map(a => ({
        apt: a,
        email: findPatientEmail(a.patient),
        beh: behandlerName(a.behandler),
        schonGesendet: !!sent[a.id],
      }))
      .sort((a, b) => b.apt.date.localeCompare(a.apt.date));
  }, [apts, tage, sent]);

  const offen = kandidaten.filter(k => !k.schonGesendet && (k.email || !serverActive()));
  const ohneMail = kandidaten.filter(k => !k.schonGesendet && !k.email && serverActive());

  const sendeAlle = async () => {
    if (busy || !offen.length) return;
    setBusy(true);
    try {
      const protokoll = { ...sent };
      // Mailjet nimmt 50 Nachrichten je Aufruf — in Blöcken senden.
      for (let i = 0; i < offen.length; i += 50) {
        const block = offen.slice(i, i + 50);
        await sendMails(block.map(k => ({
          to: k.email || "demo@dida.local",
          toName: k.apt.patient,
          subject: "Wie war Ihr Besuch bei uns? — 1 Minute Feedback",
          text: `Liebe/r ${k.apt.patient},\n\nvielen Dank für Ihren Besuch am ${k.apt.date.split("-").reverse().join(".")}! Uns interessiert, wie zufrieden Sie waren — die Beantwortung dauert unter einer Minute und ist anonym:\n\n${umfrageUrl(k.beh)}\n\nHerzlichen Dank!\n\nMit freundlichen Grüßen\n${praxisAbsender()}`,
        })));
        block.forEach(k => { protokoll[k.apt.id] = isoDay(0); });
      }
      // Erst nach erfolgreichem Versand protokollieren — bricht der Versand
      // ab, gelten die restlichen Termine weiterhin als offen.
      await saveSetting(SENT_KEY, protokoll);
      setSent(protokoll);
      toast(serverActive() ? `${offen.length} Umfrage-Mails gesendet ✓` : "Demo — Versand simuliert");
    } catch (e) {
      toast(`Versand fehlgeschlagen: ${e.message}`, "r");
    } finally { setBusy(false); }
  };

  if (!supabaseUrl) return null;

  return (
    <Card>
      <CardHead title="Umfrage nach Termin" icon="ti-mail-fast"
        right={
          <div style={{display:"flex",gap:4}}>
            {WINDOWS.map(w => (
              <button key={w} onClick={()=>setTage(w)}
                style={{fontSize:10,padding:"3px 9px",borderRadius:20,border:"0.5px solid var(--mf-border2)",
                  background:tage===w?"#0F6E56":"var(--mf-bg-alt)",color:tage===w?"#E1F5EE":"var(--mf-muted)",
                  cursor:"pointer",fontFamily:"inherit"}}>{w} Tage</button>
            ))}
          </div>
        }/>
      <div style={{fontSize:11,color:"var(--mf-muted)",marginBottom:10,lineHeight:1.5}}>
        Patienten mit einem stattgefundenen Termin der letzten {tage} Tage. Der Behandler des Termins steckt im Link — die Antwort wird ihm automatisch zugeordnet. Jeder Patient bekommt die Umfrage nur einmal.
      </div>

      {kandidaten.length===0 ? (
        <Insight>In den letzten {tage} Tagen gab es keine stattgefundenen Termine — sobald welche im Terminplan stehen, erscheinen sie hier.</Insight>
      ) : (
        <>
          <Tbl heads={["Termin","Patient","Behandler","Status"]}>
            {kandidaten.map(k=>(
              <tr key={k.apt.id}>
                <Td color="var(--mf-muted)">{k.apt.date.split("-").reverse().join(".")}</Td>
                <Td bold>{k.apt.patient}</Td>
                <Td color="var(--mf-muted)">{k.beh||"—"}</Td>
                <Td>
                  {k.schonGesendet ? <Sp color="g">gesendet {String(sent[k.apt.id]||"").split("-").reverse().join(".")}</Sp>
                    : (!k.email && serverActive()) ? <Sp color="a">keine E-Mail</Sp>
                    : <Sp color="b">bereit</Sp>}
                </Td>
              </tr>
            ))}
          </Tbl>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginTop:10}}>
            <Btn color="g" lg onClick={sendeAlle}
              style={busy||!offen.length?{opacity:.45,cursor:"default",boxShadow:"none"}:{}}>
              {busy?"Sendet …":offen.length?`${offen.length} ${offen.length===1?"Umfrage":"Umfragen"} senden`:"Alles versendet"}
            </Btn>
            {ohneMail.length>0&&(
              <span style={{fontSize:10.5,color:"var(--mf-muted)"}}>
                {ohneMail.length} {ohneMail.length===1?"Patient hat":"Patienten haben"} keine E-Mail hinterlegt — in der Patientenliste nachtragen.
              </span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
